import { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useCustomFields } from "@/hooks/useCustomFields";

interface ExpandableCustomFieldsProps {
  workspaceId: string;
  values: Record<string, string>;
  onChange: (fieldId: string, value: string) => void;
}

export function ExpandableCustomFields({ workspaceId, values, onChange }: ExpandableCustomFieldsProps) {
  const [expanded, setExpanded] = useState(false);
  const { customFields: fields, loading } = useCustomFields(workspaceId);

  const renderFieldInput = (field: { id: string; label: string; data_type: string }) => {
    switch (field.data_type) {
      case 'text':
        return (
          <Input
            value={values[field.id] || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            className="h-7 text-xs"
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={values[field.id] || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            className="h-7 text-xs"
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            value={values[field.id] || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            className="h-7 text-xs"
          />
        );
      case 'boolean':
        return (
          <Switch
            checked={values[field.id] === 'true'}
            onCheckedChange={(checked) => onChange(field.id, checked.toString())}
          />
        );
      default:
        return (
          <Input
            value={values[field.id] || ''}
            onChange={(e) => onChange(field.id, e.target.value)}
            className="h-7 text-xs"
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setExpanded(!expanded)}
        className="h-7 text-xs text-muted-foreground px-0 hover:bg-transparent"
      >
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 mr-1.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 mr-1.5" />
        )}
        More fields
      </Button>

      {expanded && (
        <div className="pt-1">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : fields.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-3">
              No custom fields defined
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {fields.map((field) => (
                <div key={field.id} className="space-y-1">
                  <label className="text-xs text-muted-foreground">
                    {field.label}
                  </label>
                  {renderFieldInput(field)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
